/**
 * ============================================================================
 * 🚀 Main Application Orchestrator (Multi-Domain Radar: Airspace & Maritime)
 * ============================================================================
 */

import './styles/map.css';
import { ApiService } from './services/api.js';
import { PlaybackLoop } from './services/playback.js';
import { RadarMap } from './components/Map.js';
import { InspectorController } from './components/Inspector.js';
import { LightboxController } from './components/Lightbox.js';
import { POLLING_INTERVAL_MS } from './utils/constants.js';

function formatDuration(totalSeconds) {
  if (totalSeconds == null || totalSeconds < 0) return '--';
  const s = Math.floor(totalSeconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function formatDurationThai(totalSeconds) {
  if (totalSeconds == null || totalSeconds < 0) return null;
  const s = Math.floor(totalSeconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days} วัน ${hours} ชม. ${mins} นาที`;
  if (hours > 0) return `${hours} ชม. ${mins} นาที`;
  if (mins > 0) return `${mins} นาที`;
  return `${s} วินาที`;
}

document.addEventListener('DOMContentLoaded', async () => {
  const api = new ApiService();
  const playback = new PlaybackLoop(api);
  const lightbox = new LightboxController();

  let activeMode = 'all'; // 'all', 'airspace', 'marine'
  let currentPreset = 'thailand'; // 'thailand' (fast regional default) or 'global' (on-demand)

  const map = new RadarMap('leaflet-map', {
    onSelect: (item) => {
      inspector.show(item);
      if (item.registration) {
        loadTrajectory(item.registration);
      }
    },
    onClearSelection: () => {
      inspector.hide();
    },
    onSelectedPlaneChange: (item) => {
      inspector.refresh(item);
    }
  });

  const inspector = new InspectorController(api, {
    onExpandPhoto: (photos, index, item) => {
      lightbox.open(photos, index, item);
    },
    onClose: () => {
      map.clearSelection();
    }
  });

  // Setup Top-Left Viewport Focus Preset Switcher (Thailand vs Global)
  setupViewportPresetSwitcher(map);

  // Setup Top-Center Radar Domain Mode Switcher
  setupModeSwitcher(map);

  // Setup Bottom-Left Basemap Switcher & Strategic Overlays
  setupLayerSwitcher(map);

  // Setup Top-Right Recorder Status Button
  setupStatusPanel(api);

  // First-load summary
  showBootHistory(api);

  // Load Real-time Strategic Infrastructure POIs & Corridors & Airspace Structure & Live Weather/Radar & Geofences & Marine State
  loadInfrastructure();
  loadCorridors();
  loadAirspace();
  loadWeather();
  loadOceanData();
  loadGeofencing();
  loadRangeRings();
  loadAnchorages();

  async function loadInfrastructure() {
    try {
      const data = await api.getAllInfrastructure();
      if (data) {
        if (data.airports) map.setAirports(data.airports);
        if (data.seaports) map.setSeaports(data.seaports);
        console.log(`[Main] Loaded ${data.airports?.length || 0} airports and ${data.seaports?.length || 0} seaports infrastructure POIs.`);
      }
    } catch (e) {
      console.warn('[Main] Infrastructure fetch failed:', e);
    }
  }

  async function loadCorridors() {
    try {
      const data = await api.getAllCorridors();
      if (data) {
        if (data.airways) map.setAirways(data.airways);
        if (data.shipping_lanes) map.setShippingLanes(data.shipping_lanes);
        console.log(`[Main] Loaded ${data.airways?.length || 0} airways and ${data.shipping_lanes?.length || 0} shipping lanes.`);
      }
    } catch (e) {
      console.warn('[Main] Corridors fetch failed:', e);
    }
  }

  async function loadAirspace() {
    try {
      const data = await api.getAllAirspace();
      if (data) {
        map.renderAirspace(data);
        console.log(`[Main] Loaded Bangkok FIR, ${data.sectors?.length || 0} TMA sectors, and ${data.waypoints?.length || 0} waypoints.`);
      }
    } catch (e) {
      console.warn('[Main] Airspace structure fetch failed:', e);
    }
  }

  async function loadWeather() {
    try {
      const data = await api.getAllWeather();
      if (data) {
        map.renderWeather(data);
        console.log(`[Main] Loaded ${data.stations?.length || 0} live NOAA METAR weather stations & RainViewer radar.`);
      }
    } catch (e) {
      console.warn('[Main] Weather fetch failed:', e);
    }
  }

  async function loadGeofencing() {
    try {
      const data = await api.getGeofencingStatus();
      if (data) {
        map.renderGeofences(data);
        console.log(`[Main] Loaded ${data.total_zones || 0} strategic geo-fencing restricted zones.`);
      }
    } catch (e) {
      console.warn('[Main] Geofencing fetch failed:', e);
    }
  }

  async function loadRangeRings() {
    try {
      const data = await api.getRangeRingsConfig(map.activeRadarCenter || 'VTBS');
      if (data) {
        map.renderRangeRings(data);
        console.log(`[Main] Loaded Range Rings config centered at ${data.center?.name || 'VTBS'}.`);
      }
    } catch (e) {
      console.warn('[Main] Range rings fetch failed:', e);
    }
  }

  async function loadAnchorages() {
    try {
      const data = await api.getAnchorageStatus();
      if (data) {
        map.renderAnchorages(data);
        console.log(`[Main] Loaded ${data.total_zones || 0} strategic marine anchorage zones.`);
      }
    } catch (e) {
      console.warn('[Main] Anchorages fetch failed:', e);
    }
  }

  async function loadOceanData() {
    try {
      const data = await api.getAllOceanZones();
      if (data && data.ocean_zones) {
        map.renderOceanData(data.ocean_zones);
        console.log(`[Main] Loaded ${data.ocean_zones.length || 0} real-time Open-Meteo marine wave & ocean current zones.`);
      }
    } catch (e) {
      console.warn('[Main] Ocean data fetch failed:', e);
    }
  }

  async function loadTrajectory(reg) {
    if (!reg) return;
    try {
      const data = await api.getTrajectory(reg);
      // 🛡️ Guard: Only draw if the user is still selecting this exact aircraft
      if (map.selectedId === `flight_${reg}` && data && data.trajectory && data.trajectory.length > 0) {
        map.drawTrail(data.trajectory);
      }
    } catch (e) {
      console.warn('[Main] Trajectory fetch failed:', e);
    }
  }

  function tickPlayback() {
    const aircraft = playback.next();
    if (aircraft.length > 0) {
      map.updateMultiDomain({ aircraft, vessels: [] });
      updateBootCounters(aircraft.length, 0);
    }
  }

  let syncTick = 0;
  // Real-time multi-domain radar sync (loads Thailand & SEA by default; worldwide on-demand)
  async function syncRadar() {
    try {
      const zoom = map && map.map ? map.map.getZoom() : 6;
      let boundsStr = null;
      let zone = currentPreset;

      if (currentPreset === 'thailand') {
        const bounds = map && map.map ? map.map.getBounds().pad(0.20) : null;
        boundsStr = bounds ? `${bounds.getSouth()},${bounds.getNorth()},${bounds.getWest()},${bounds.getEast()}` : null;
      } else {
        zone = 'global';
      }

      const data = await api.getCombinedRadar(activeMode, { bounds: boundsStr, zoom, zone });
      if (data) {
        const aircraft = data.aircraft || [];
        const vessels = data.vessels || [];
        const totalAir = data.total_aircraft ?? aircraft.length;
        const totalSea = data.total_vessels ?? vessels.length;
        const totalDrones = data.total_drones ?? aircraft.filter(p => p.is_drone).length;

        // Update Badges on Mode Switcher
        const badgeAll = document.getElementById('badge-all-count');
        const badgeAir = document.getElementById('badge-air-count');
        const badgeSea = document.getElementById('badge-marine-count');
        const badgeDrone = document.getElementById('badge-drone-count');
        if (badgeAll) badgeAll.textContent = (totalAir + totalSea).toString();
        if (badgeAir) badgeAir.textContent = totalAir.toString();
        if (badgeSea) badgeSea.textContent = totalSea.toString();
        if (badgeDrone) badgeDrone.textContent = totalDrones.toString();

        map.updateMultiDomain({ aircraft, vessels, timestamp: data.timestamp });
        updateBootCounters(totalAir, totalSea, true);

        // Throttle heavier spatial overlays (Heatmap & Projections) to every 5s
        syncTick++;
        if (syncTick % 5 === 1) {
          api.getTrafficDensity()
            .then(densityData => map.updateDensityHeatmap(densityData))
            .catch(err => console.warn('[Main] Density sync error:', err));

          api.getProjections()
            .then(projData => map.updateProjections(projData))
            .catch(err => console.warn('[Main] Projection sync error:', err));
        }

        return;
      }
      tickPlayback();
    } catch (e) {
      console.warn('[Main] Combined fetch failed, attempting fallback:', e);
      updateBootCounters(0, 0, false);
      tickPlayback();
    }
  }

  function updateBootCounters(airCount, seaCount, isOnline = true) {
    const centerEl = document.getElementById('boot-center-count');
    const cornerEl = document.getElementById('boot-corner-count');
    const cornerBadge = document.getElementById('boot-corner-badge');
    
    if (!isOnline) {
      if (cornerEl) cornerEl.textContent = 'CONNECTING TO RADAR SERVER...';
      if (cornerBadge) cornerBadge.classList.add('reconnecting');
      return;
    }

    if (cornerBadge) cornerBadge.classList.remove('reconnecting');

    let txt = '';
    if (activeMode === 'airspace') {
      txt = `TRACKING ${airCount} AIRCRAFT`;
    } else if (activeMode === 'marine') {
      txt = `TRACKING ${seaCount} VESSELS`;
    } else {
      txt = `TRACKING ${airCount} AIRCRAFT · ${seaCount} VESSELS`;
    }

    if (centerEl) centerEl.textContent = txt;
    if (cornerEl) cornerEl.textContent = `SYSTEM ONLINE · ${txt}`;
  }

  function setupViewportPresetSwitcher(mapInstance) {
    const btnTh = document.getElementById('preset-btn-thailand');
    const btnGlobal = document.getElementById('preset-btn-global');
    if (!btnTh || !btnGlobal) return;

    btnTh.addEventListener('click', (e) => {
      e.stopPropagation();
      currentPreset = 'thailand';
      btnTh.classList.add('active');
      btnGlobal.classList.remove('active');
      if (mapInstance && mapInstance.map) {
        mapInstance.map.flyTo([13.7367, 100.5231], 6, {
          duration: 1.2,
          easeLinearity: 0.25
        });
      }
      syncRadar();
    });

    btnGlobal.addEventListener('click', (e) => {
      e.stopPropagation();
      currentPreset = 'global';
      btnGlobal.classList.add('active');
      btnTh.classList.remove('active');
      if (mapInstance && mapInstance.map) {
        mapInstance.map.flyTo([20.0, 15.0], 2, {
          duration: 1.4,
          easeLinearity: 0.25
        });
      }
      syncRadar();
    });

    // Auto sync active pill button with user manual zoom level
    if (mapInstance && mapInstance.map) {
      mapInstance.map.on('zoomend', () => {
        const z = mapInstance.map.getZoom();
        if (z <= 3) {
          currentPreset = 'global';
          btnGlobal.classList.add('active');
          btnTh.classList.remove('active');
        } else if (z >= 5) {
          currentPreset = 'thailand';
          btnTh.classList.add('active');
          btnGlobal.classList.remove('active');
        }
      });
    }
  }

  function setupModeSwitcher(mapInstance) {
    const switcher = document.getElementById('radar-mode-switcher');
    if (!switcher) return;

    const btns = switcher.querySelectorAll('.mode-pill-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = btn.dataset.mode || 'all';
        activeMode = mode;
        btns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        mapInstance.setMode(mode);
        syncRadar();
      });
    });
  }

  async function showBootHistory(apiService) {
    const historyEl = document.getElementById('boot-center-history');
    if (!historyEl) return;
    try {
      const stats = await apiService.getStats();
      const live = stats.airspace_live || {};
      historyEl.textContent =
        `ระบบสตรีมสด Real-Time (Pure Live Radar) · เครื่องบินสดในจอ ${live.active_now ?? 0} ลำ · ข้อมูลลบอัตโนมัติเมื่อลงจอด`;
    } catch (e) {
      // Best-effort
    }
  }

  function setupLayerSwitcher(mapInstance) {
    const toggleBtn = document.getElementById('layer-toggle-btn');
    const popover = document.getElementById('layer-menu-popover');
    if (!toggleBtn || !popover) return;

    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      popover.classList.toggle('hidden');
    });

    document.addEventListener('click', (e) => {
      if (!popover.contains(e.target) && e.target !== toggleBtn) {
        popover.classList.add('hidden');
      }
    });

    const optionBtns = popover.querySelectorAll('.layer-option-btn');
    optionBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const layerKey = btn.dataset.layer;
        if (!layerKey) return;

        optionBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        mapInstance.setTileLayer(layerKey);
        popover.classList.add('hidden');
      });
    });

    // Strategic Infrastructure Overlays Toggles
    const toggleAirportsBtn = document.getElementById('toggle-layer-airports');
    if (toggleAirportsBtn) {
      toggleAirportsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleAirports();
        toggleAirportsBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleSeaportsBtn = document.getElementById('toggle-layer-seaports');
    if (toggleSeaportsBtn) {
      toggleSeaportsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleSeaports();
        toggleSeaportsBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleAirwaysBtn = document.getElementById('toggle-layer-airways');
    if (toggleAirwaysBtn) {
      toggleAirwaysBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleAirways();
        toggleAirwaysBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleShippingLanesBtn = document.getElementById('toggle-layer-shipping-lanes');
    if (toggleShippingLanesBtn) {
      toggleShippingLanesBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleShippingLanes();
        toggleShippingLanesBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleFIRBtn = document.getElementById('toggle-layer-fir');
    if (toggleFIRBtn) {
      toggleFIRBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleFIRSectors();
        toggleFIRBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleWaypointsBtn = document.getElementById('toggle-layer-waypoints');
    if (toggleWaypointsBtn) {
      toggleWaypointsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleWaypoints();
        toggleWaypointsBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleWeatherBtn = document.getElementById('toggle-layer-weather');
    if (toggleWeatherBtn) {
      toggleWeatherBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleWeather();
        toggleWeatherBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleRainRadarBtn = document.getElementById('toggle-layer-rain-radar');
    if (toggleRainRadarBtn) {
      toggleRainRadarBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleRainRadar();
        toggleRainRadarBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleHeatmapBtn = document.getElementById('toggle-layer-heatmap');
    if (toggleHeatmapBtn) {
      toggleHeatmapBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleHeatmap();
        toggleHeatmapBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleProjectionsBtn = document.getElementById('toggle-layer-projections');
    if (toggleProjectionsBtn) {
      toggleProjectionsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleProjections();
        toggleProjectionsBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleGeofenceBtn = document.getElementById('toggle-layer-geofence');
    if (toggleGeofenceBtn) {
      toggleGeofenceBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleGeofences();
        toggleGeofenceBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleRangeRingsBtn = document.getElementById('toggle-layer-range-rings');
    if (toggleRangeRingsBtn) {
      toggleRangeRingsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleRangeRings();
        toggleRangeRingsBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleAnchorageBtn = document.getElementById('toggle-layer-anchorage');
    if (toggleAnchorageBtn) {
      toggleAnchorageBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleAnchorages();
        toggleAnchorageBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleOceanBtn = document.getElementById('toggle-layer-ocean');
    if (toggleOceanBtn) {
      toggleOceanBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = mapInstance.toggleOceanData();
        toggleOceanBtn.classList.toggle('active', isVisible);
      });
    }

    const toggleMeasureBtn = document.getElementById('toggle-tool-measure');
    if (toggleMeasureBtn) {
      toggleMeasureBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isMeasuring = mapInstance.toggleMeasureMode();
        toggleMeasureBtn.classList.toggle('active', isMeasuring);
      });
    }
  }

  function setupStatusPanel(apiService) {
    const toggleBtn = document.getElementById('status-toggle-btn');
    const popover = document.getElementById('status-popover');
    const rowsEl = document.getElementById('status-rows');
    const dotEl = document.getElementById('status-popover-dot');
    const closeBtn = document.getElementById('status-popover-close');
    if (!toggleBtn || !popover || !rowsEl) return;

    let refreshTimer = null;
    let tickerTimer = null;
    let baseAge = 0;
    let lastFetchTime = 0;
    let lastUpdatedTimeStr = '';

    function row(label, value, cls, id) {
      return `
        <div class="status-row">
          <span class="status-row-label">${label}</span>
          <span class="status-row-value ${cls || ''}" ${id ? `id="${id}"` : ''}></span>
        </div>
      `;
    }

    function renderRows(entries) {
      rowsEl.innerHTML = entries.map(([label, , cls, id]) => row(label, '', cls, id)).join('');
      const valueEls = rowsEl.querySelectorAll('.status-row-value');
      entries.forEach(([, value], i) => {
        if (valueEls[i]) valueEls[i].textContent = value;
      });
    }

    function updateAgeTicker() {
      const ageEl = document.getElementById('status-age-ticker');
      if (!ageEl || !lastFetchTime) return;
      const elapsed = (Date.now() - lastFetchTime) / 1000;
      const currentAge = Math.max(0, baseAge + elapsed);
      ageEl.textContent = `${currentAge.toFixed(1)} วินาทีที่แล้ว (${lastUpdatedTimeStr})`;
    }

    async function refreshStats() {
      try {
        const stats = await apiService.getStats();
        const rec = stats.recorder || {};
        const db = stats.database || {};
        const live = stats.airspace_live || {};
        const coll = stats.collection || {};

        const isActive = !!rec.is_active;
        toggleBtn.classList.toggle('is-offline', !isActive);
        if (dotEl) dotEl.style.background = isActive ? 'var(--color-accent)' : 'var(--color-critical)';

        baseAge = rec.last_snapshot_age_seconds != null ? rec.last_snapshot_age_seconds : 0;
        lastFetchTime = Date.now();
        lastUpdatedTimeStr = live.last_updated ? live.last_updated.split(' ')[1] : new Date().toLocaleTimeString();

        renderRows([
          ['Platform Version', `v${stats.version || '7.2.0'}`, 'accent'],
          ['Radar Architecture', 'PURE LIVE STREAM', 'accent'],
          ['Flight Radar', isActive ? 'ACTIVE (Real-Time)' : 'OFFLINE', isActive ? 'accent' : 'critical'],
          ['Marine AIS Feed', 'ACTIVE (AISStream)', 'accent'],
          ['อัปเดตล่าสุด', `${baseAge.toFixed(1)} วินาทีที่แล้ว (${lastUpdatedTimeStr})`, 'accent', 'status-age-ticker'],
          ['Air Zone', (rec.zone || '-').toUpperCase()],
          ['Airborne Tracked Now', `${live.active_now ?? 0} ลำ`, 'accent'],
          ['World Total Flights', `${live.world_total?.toLocaleString() ?? 0}`],
          ['Radar Scan Cycle', rec.scan_interval_seconds ? `ทุก ${rec.scan_interval_seconds} วินาที` : 'ทุก 5.0 วินาที'],
          ['Data Persistence', 'RAM-ONLY (No Bloat)', 'accent'],
          ['Landing Action', 'AUTO-PURGE ON TOUCHDOWN', 'accent'],
          ['Database Size', `${db.database_size_mb ?? 0} MB`]
        ]);
      } catch (e) {
        toggleBtn.classList.add('is-offline');
        if (dotEl) dotEl.style.background = 'var(--color-critical)';
        renderRows([['Recorder', 'UNREACHABLE', 'critical']]);
      }
    }

    function openPopover() {
      popover.classList.remove('hidden');
      refreshStats();
      if (!refreshTimer) refreshTimer = setInterval(refreshStats, 3000);
      if (!tickerTimer) tickerTimer = setInterval(updateAgeTicker, 250);
    }

    function closePopover() {
      popover.classList.add('hidden');
      if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
      }
      if (tickerTimer) {
        clearInterval(tickerTimer);
        tickerTimer = null;
      }
    }

    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (popover.classList.contains('hidden')) openPopover();
      else closePopover();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closePopover();
      });
    }

    document.addEventListener('click', (e) => {
      if (!popover.contains(e.target) && e.target !== toggleBtn && !popover.classList.contains('hidden')) {
        closePopover();
      }
    });

    // openPopover(); // Open on user click, keep initial UI clean
  }

  // 1. Fast First-Paint (Immediate Viewport-First Sync in <50ms)
  async function initialFastSync() {
    try {
      const bounds = map.map ? map.map.getBounds().pad(0.15) : null;
      const boundsStr = bounds ? `${bounds.getSouth()},${bounds.getNorth()},${bounds.getWest()},${bounds.getEast()}` : null;
      const zoom = map.map ? map.map.getZoom() : 6;

      const firstData = await api.getCombinedRadar(activeMode, { bounds: boundsStr, zoom, zone: 'thailand' });
      if (firstData) {
        const totalAir = firstData.total_aircraft ?? (firstData.aircraft || []).length;
        const totalSea = firstData.total_vessels ?? (firstData.vessels || []).length;
        const totalDrones = firstData.total_drones ?? (firstData.aircraft || []).filter(p => p.is_drone).length;

        const badgeAll = document.getElementById('badge-all-count');
        const badgeAir = document.getElementById('badge-air-count');
        const badgeSea = document.getElementById('badge-marine-count');
        const badgeDrone = document.getElementById('badge-drone-count');
        if (badgeAll) badgeAll.textContent = (totalAir + totalSea).toString();
        if (badgeAir) badgeAir.textContent = totalAir.toString();
        if (badgeSea) badgeSea.textContent = totalSea.toString();
        if (badgeDrone) badgeDrone.textContent = totalDrones.toString();

        map.updateMultiDomain({
          aircraft: firstData.aircraft || [],
          vessels: firstData.vessels || [],
          timestamp: firstData.timestamp
        });
        updateBootCounters(totalAir, totalSea, true);
      }
    } catch (e) {
      console.warn('[Main] Fast initial sync fallback:', e);
    }
    // Seamless full background sync
    syncRadar();
  }

  initialFastSync();
  setInterval(syncRadar, POLLING_INTERVAL_MS);

  // Instant Viewport-First Sync on User Pan/Zoom
  if (map && map.map) {
    let panDebounce = null;
    map.map.on('moveend zoomend', () => {
      clearTimeout(panDebounce);
      panDebounce = setTimeout(() => {
        syncRadar();
      }, 150);
    });
  }

  // Background fallback preload (Delayed by 5s to keep initial load 100% instant)
  setTimeout(() => {
    playback.load(5)
      .then(frameCount => console.log(`[Main] Loaded ${frameCount} recorded frames as a fallback.`))
      .catch(e => console.warn('[Main] Playback fallback load failed:', e));
  }, 5000);
});
